import { yupResolver } from '@hookform/resolvers/yup';
import UserCheckSvg from 'components/_common/Icons/SVGIcons/UserCheckSvg';
import { placeParamsToURL } from 'helpers/CommonHelper';
import { CUSTOMER_SLUG, FFL_SLUG } from 'helpers/DataHelper';
import { emailREGEX } from 'helpers/ValidationHelper';
import { t } from 'i18next';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { loginRoutes } from 'routes/admin/AdminRoutes';
import { executeLoginFlow } from 'store/auth/actions/AuthAction';
import * as Yup from 'yup';

const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#9b9daf', cursor: 'pointer' }}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#9b9daf', cursor: 'pointer' }}
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

interface LoginFormSectionProps {
  activeTab: 'buyer' | 'dealer';
  onClose: () => void;
  stayOnPageAfterLogin?: boolean;
  onSwitchToSignUp: (targetTab?: 'buyer' | 'dealer') => void;
  onSwitchToLogin: (targetTab?: 'buyer' | 'dealer') => void;
}

const LoginFormSection: React.FC<LoginFormSectionProps> = ({
  activeTab,
  onClose,
  stayOnPageAfterLogin,
  onSwitchToSignUp,
  onSwitchToLogin,
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const defaultValue = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t('required_error_msg', { field_name: t('email_address') }))
      .matches(emailREGEX, t('invalid_error_msg', { field_name: t('email_address') })),
    password: Yup.string().required(t('required_error_msg', { field_name: t('password') })),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValue,
  });

  const onSubmit = async (formData: any) => {
    if (loading) return;
    const activeRoleSlug = activeTab === 'buyer' ? CUSTOMER_SLUG : FFL_SLUG;
    await executeLoginFlow({ ...formData, role_slug: activeRoleSlug }, setLoading, {
      onClose,
      navigate,
      stayOnPageForCustomer: stayOnPageAfterLogin,
    });
  };

  return (
    <form className="login-modal-content__form" onSubmit={handleSubmit(onSubmit)}>
      {activeTab === 'buyer' ? (
        <div className="form-heading-section">
          <h3>{t('welcome_back')}</h3>
          <p>{t('login_to_buy_and_manage')}</p>
        </div>
      ) : (
        <div className="form-heading-section">
          <h3>{t('ffl_dealer_sign_in')}</h3>
          <p>{t('access_your_dealer_dashboard')}</p>
        </div>
      )}

      {/* Email */}
      <div className="input-group-custom">
        <label htmlFor="modal-email">
          {t('email_address')} <span className="text-danger">*</span>
        </label>
        <input
          id="modal-email"
          type="email"
          {...register('email')}
          autoComplete="off"
          placeholder={t('email_address')}
          className={errors.email ? 'input-error' : ''}
        />
        {errors.email && <span className="error-message">{errors.email.message}</span>}
      </div>

      {/* Password */}
      <div className="input-group-custom">
        <label htmlFor="modal-password">
          {t('password')} <span className="text-danger">*</span>
        </label>
        <div className="password-input-wrapper">
          <input
            id="modal-password"
            type={showPassword ? 'text' : 'password'}
            {...register('password')}
            autoComplete="new-password"
            placeholder={t('password')}
            className={errors.password ? 'input-error' : ''}
          />
          <span className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
          </span>
        </div>
        {errors.password && <span className="error-message">{errors.password.message}</span>}
      </div>

      {/* Remember and Forgot password */}
      <div className="forgot-remember-row">
        <label className="remember-checkbox-label">
          <input type="checkbox" className="custom-checkbox" />
          <span>{t('keep_me_signed_in')}</span>
        </label>
        <Link
          to={placeParamsToURL(loginRoutes.forgotPassword.path)}
          onClick={onClose}
          className="forgot-password-link"
        >
          {t('forgot_password?')}
        </Link>
      </div>

      {/* Login Button */}
      <button
        type="submit"
        className="btn btn-orange login-modal-content__submit-btn"
        disabled={loading}
      >
        {loading ? t('loading') : t('login')}
      </button>

      {/* Signup Suggestion */}
      <div className="form-signup-prompt">
        {activeTab === 'buyer' ? (
          <p>
            {t('new_to_mlgs')}{' '}
            <span
              onClick={() => onSwitchToSignUp('buyer')}
              className="orange-link"
              style={{ cursor: 'pointer' }}
            >
              {t('create_an_account')}
            </span>
          </p>
        ) : (
          <p>
            {t('dont_have_ffl_access_yet')}{' '}
            <span
              onClick={() => onSwitchToSignUp('dealer')}
              className="orange-link"
              style={{ cursor: 'pointer' }}
            >
              {t('apply_now')}
            </span>
          </p>
        )}
      </div>

      {/* Footer Promotion Banner */}
      <div className="form-promo-banner">
        {activeTab === 'buyer' ? (
          <div className="promo-left">
            <div className="promo-icon-wrap">
              <UserCheckSvg />
            </div>
            <div className="promo-text">
              <div className="promo-text-content">
                <h5>{t('are_you_an_ffl_dealer_q')}</h5>
                <p>{t('join_our_network')}</p>
              </div>
              <span
                onClick={() => onSwitchToSignUp('dealer')}
                className="btn-promo-outline"
                style={{ cursor: 'pointer' }}
              >
                {t('join_as_ffl')}
              </span>
            </div>
          </div>
        ) : (
          <div className="promo-left">
            <div className="promo-icon-wrap">
              <UserCheckSvg />
            </div>
            <div className="promo-text">
              <div className="promo-text-content">
                <h5>{t('explore_customer_benefits')}</h5>
                <p>{t('track_items_promo')}</p>
              </div>
              <span
                onClick={() => onSwitchToLogin('buyer')}
                className="btn-promo-outline"
                style={{ cursor: 'pointer' }}
              >
                {t('buyer_login')}
              </span>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default LoginFormSection;
